import axios from 'axios';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
// api
import ApiKey from '../../api/ApiKey';
import BaseApi from '../../api/BaseApi';
// components
import Sidebar from '../Sidebar';
import Footer from '../../element/Footer';
import NavBar from '../../element/NavBar';
// helpers
import { logOutIfTokenExpired, resultInfo } from '../../helpers';
// mui
import DeleteIcon from '@mui/icons-material/Delete';
import FolderIcon from '@mui/icons-material/Folder';
import PersonIcon from '@mui/icons-material/Person';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';

const FavouriteList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('global');
  const userType = Cookies.get('user_type');
  const tokenKey = Cookies.get('tokenClient');

  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [folderName, setFolderName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isExpanded, setIsExpanded] = useState(false);
  const [foldersList, setFoldersList] = useState(null);
  const [favouriteData, setFavouriteData] = useState([]);

  const visibleItems = 4;

  const toggleView = () => {
    setIsExpanded(prev => !prev);
  };

  const dataPerPage = 10;

  const indexOfLastRecord = currentPage * dataPerPage;
  const indexOfFirstRecord = indexOfLastRecord - dataPerPage;

  const handlePageChange = async pageNumber => {
    try {
      window.scrollTo(0, 0);
      setLoading(true);

      const favouritesListResponse = await axios.post(
        BaseApi + '/candidates/favorite',
        null,
        {
          params: {
            page: pageNumber,
            per_page: dataPerPage,
          },
          headers: {
            key: ApiKey,
            token: tokenKey,
            contentType: 'application/json',
          },
        },
      );

      setLoading(false);

      if (favouritesListResponse.data.status === 200) {
        setIsExpanded(false);
        setFavouriteData(favouritesListResponse.data.response.Favorite);
        setTotalCount(favouritesListResponse.data.response.Favorite.total);
        setCurrentPage(favouritesListResponse.data.response.Favorite.current_page);
      } else if (favouritesListResponse.data.status === 400) {
        navigate('/');

        Cookies.remove('fname');
        Cookies.remove('user_id');
        Cookies.remove('user_type');
        Cookies.remove('tokenClient');

        resultInfo('warning', favouritesListResponse.data.message, t('employerFavouriteList.close'));
      } else {
        Swal.fire({
          icon: 'error',
          text: favouritesListResponse.data.message,
          title: t('employerFavouriteList.removeFolderFailedTxt'),
          confirmButtonText: t('employerFavouriteList.close'),
        });
      }
    } catch (error) {
      setLoading(false);

      if (error.message === 'Network Error' || error.response.data.message === 'Token has expired') {
        navigate('/');

        logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
      }

      console.error(error);
    }
  };

  const getData = async () => {
    try {
      setLoading(true);

      const favouritesListResponse = await axios.post(
        BaseApi + '/candidates/favorite',
        null,
        {
          params: {
            page: currentPage,
            per_page: dataPerPage,
          },
          headers: {
            key: ApiKey,
            token: tokenKey,
            contentType: 'application/json',
          },
        },
      );

      const foldersListResponse = await axios.get(
        BaseApi + '/folder/list/count_cv',
        {
          headers: {
            key: ApiKey,
            token: tokenKey,
            contentType: 'application/json',
          },
        },
      );

      setLoading(false);

      if (favouritesListResponse.data.status === 200) {
        setFoldersList(foldersListResponse.data);
        setFavouriteData(favouritesListResponse.data.response.Favorite);
        setTotalCount(favouritesListResponse.data.response.Favorite.total);
        setCurrentPage(favouritesListResponse.data.response.Favorite.current_page);
      } else if (favouritesListResponse.data.status === 400) {
        navigate('/');

        Cookies.remove('fname');
        Cookies.remove('user_id');
        Cookies.remove('user_type');
        Cookies.remove('tokenClient');

        resultInfo('warning', favouritesListResponse.data.message, t('employerFavouriteList.close'));
      } else {
        Swal.fire({
          icon: 'error',
          text: favouritesListResponse.data.message,
          title: t('employerFavouriteList.removeFolderFailedTxt'),
          confirmButtonText: t('employerFavouriteList.close'),
        });
      }
    } catch (error) {
      setLoading(false);

      if (error.message === 'Network Error' || error.response.data.message === 'Token has expired') {
        navigate('/');

        logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
      }

      console.error(error);
    }
  };

  const handleChange = e => setFolderName(e.target.value);

  const handleCreateFolder = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        BaseApi + '/folder/create',
        {
          name: folderName,
        },
        {
          headers: {
            key: ApiKey,
            token: tokenKey,
            contentType: 'application/json',
          },
        },
      );

      setLoading(false);

      if (response.status === 200) {
        resultInfo('success', t('employerFavouriteList.folderCreated'), t('employerFavouriteList.close'));
        setFolderName('');
        getData();
      } else {
        Swal.fire({
          icon: 'error',
          text: response.data.message,
          title: t('employerFavouriteList.createFolderFailedTitle'),
          confirmButtonText: t('employerFavouriteList.close'),
        });
      }
    } catch (error) {
      setLoading(false);

      if (error.message === 'Network Error' || error.response.data.message === 'Token has expired') {
        navigate('/');

        logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
      } else {
        Swal.fire({
          icon: 'error',
          text: t('employerFavouriteList.folderFailedTxt'),
          confirmButtonText: t('employerFavouriteList.close'),
          title: t('employerFavouriteList.createFolderFailedTitle'),
        });
      }

      console.error('Couldn\'t create the folder!', error);
    }
  };

  const handleDelete = async id => {
    try {
      const confirmationResult = await Swal.fire({
        icon: 'question',
        showCancelButton: true,
        cancelButtonText: t('employerFavouriteList.no'),
        confirmButtonText: t('employerFavouriteList.yes'),
        text: t('employerFavouriteList.removeProfileConfirmTxt'),
        title: t('employerFavouriteList.removeProfileConfirmTitle'),
      });

      if (confirmationResult.isConfirmed) {
        setLoading(true);

        const response = await axios.post(
          BaseApi + `/candidates/deleteFavoriteList/${id}`,
          null,
          {
            headers: {
              'Content-Type': 'application/json',
              key: ApiKey,
              token: tokenKey,
            },
          },
        );

        setLoading(false);

        if (response.data.status === 200) {
          resultInfo('success', t('employerFavouriteList.removeSuccessTitle'), t('employerFavouriteList.close'));
        } else if (response.data.status === 400) {
          navigate('/');

          Cookies.remove('fname');
          Cookies.remove('user_id');
          Cookies.remove('user_type');
          Cookies.remove('tokenClient');

          resultInfo('warning', response.data.message, t('employerFavouriteList.close'));
        } else {
          Swal.fire({
            icon: 'error',
            text: response.data.message,
            title: t('employerFavouriteList.removeFailedTitle'),
            confirmButtonText: t('employerFavouriteList.close'),
          });
        }

        getData();
      }
    } catch (error) {
      setLoading(false);

      if (error.message === 'Network Error' || error.response.data.message === 'Token has expired') {
        navigate('/');

        logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
      } else {
        Swal.fire({
          icon: 'error',
          text: t('employerFavouriteList.removeFailedTxt'),
          confirmButtonText: t('employerFavouriteList.close'),
          title: t('employerFavouriteList.removeFailedTitle'),
        });
      }

      console.error('Couldn\'t delete the record!', error);
    }
  };

  useEffect(() => {
    if (userType === 'candidate' || !tokenKey) {
      navigate('/user/employerlogin');
    } else {
      getData();

      window.scrollTo(0, 0);
    }
  }, [tokenKey, navigate, userType]); // eslint-disable-line

  return (
    <>
      <NavBar />
      <div className='container favouriteList'>
        <div className='row'>
          <div className='col-lg-3 col-md-3'>
            <Sidebar />
          </div>
          {
            loading
              ? (
                <div className='loader-container'></div>
              ) : (
                <>
                  <div className='col-lg-9 col-md-9 mb-5'>
                    <div className='FLHeader'>
                      <div className='mb-4 d-flex PageHeader'>
                        <img src='/Images/employerSide/icon4color.png' alt='' />
                        <h3 className='mx-2'>
                          {t('employerFavouriteList.favouriteJobseeker')}
                        </h3>
                      </div>
                      <div className={`${foldersList?.length > 0 ? 'mb-4' : ''}`}>
                        <div className='mb-4 d-flex align-items-center justify-content-between'>
                          <p className='fs-5'>{t('employerFavouriteList.folders')}</p>
                          <div className='dropdown'>
                            <button
                              type='button'
                              aria-expanded='false'
                              className='btn button1'
                              data-bs-toggle='dropdown'
                            >
                              <CreateNewFolderIcon sx={{ mr: '8px' }} />
                              {t('employerFavouriteList.addFolder')}
                            </button>
                            <div className='p-4 dropdown-menu dropdown-menu-end'>
                              <input
                                type='text'
                                name='folderName'
                                value={folderName}
                                onChange={handleChange}
                                className='form-control createFolderInput'
                                placeholder={t('employerFavouriteList.folderName')}
                              />
                              <button
                                disabled={!folderName}
                                onClick={handleCreateFolder}
                                className='favouriteListFoldersButton'
                              >
                                {t('employerFavouriteList.create')}
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className='favouriteListFoldersBox'>
                          {
                            foldersList
                              ?.slice(0, isExpanded ? foldersList?.length : visibleItems)
                              .map(i => (
                                <Link key={i.id} title={i.name} to={`/user/favouritelist/${i.slug}?folder_id=${i.id}`}>
                                  <div className='favouriteListFolder'>
                                    <div className='d-flex'>
                                      <FolderIcon sx={{ mr: '8px', color: '#818181' }} />
                                      <p className='favouriteListFolderName'>{i.name}</p>
                                      <p className='m-0'>({i.favourites_count})</p>
                                    </div>
                                  </div>
                                </Link>
                              ))
                          }
                        </div>
                        {
                          foldersList?.length > visibleItems && (
                            <button onClick={toggleView} className='favouriteListFoldersButton'>
                              {isExpanded ? t('employerFavouriteList.hide') : t('employerFavouriteList.show')}
                            </button>
                          )
                        }
                      </div>
                      <p className='fs-5'>{t('employerFavouriteList.allResumes')} {`(${totalCount})`}</p>
                      <div className='PHBody mt-4'>
                        <table className='table'>
                          <thead>
                            <tr className='table-active TrFirst'>
                              <th className='' scope='col p-3'>
                                {t('employerFavouriteList.SNo.')}
                              </th>
                              <th className='' scope='col p-3'>
                                {t('employerFavouriteList.jobseekerName')}
                              </th>
                              <th className='' scope='col p-3'>
                                {t('employerFavouriteList.resumeName')}
                              </th>
                              <th className='' scope='col p-3'>
                                {t('employerFavouriteList.action')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {
                              favouriteData?.data?.map((i, index) => (
                                <tr key={i.id} className='TrDefoult'>
                                  <td className=''>{currentPage - 1 > 0 ? currentPage - 1 : ''}{index +1}</td>
                                  <td className=''>
                                    {i.first_name} {i.last_name}
                                  </td>
                                  <td className=''>{i.position_name}</td>
                                  <td className='APActionButton'>
                                    <button
                                      className='btn-primary button1'
                                      onClick={
                                        () => i.from_cv_database === 1
                                          ? navigate(`/candidates/listing/${i.cv_id}`)
                                          : navigate(`/candidates/profile/${i.slug}?cv_id=${i.cv_id}`)
                                      }
                                    >
                                      <PersonIcon />
                                    </button>
                                    <button
                                      className='btn-secondary button1'
                                      onClick={() => handleDelete(i.id)}
                                    >
                                      <DeleteIcon />
                                    </button>
                                  </td>
                                </tr>
                              ))
                            }
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className='favListPagination'>
                      <p className='text-muted empPaginationData'>
                        {t('pagination.NoofRecords')} {favouriteData?.data?.length > 0 ? indexOfFirstRecord + 1 : indexOfFirstRecord}-
                        {Math.min(indexOfLastRecord, totalCount)} of{' '}
                        {totalCount}
                      </p>
                      <div className='d-flex justify-content-center empPaginationButton'>
                        <button
                          className='navButton1 me-2'
                          disabled={currentPage === 1}
                          onClick={() => handlePageChange(currentPage - 1)}
                        >
                          {t('pagination.Prev')}
                        </button>
                        <button
                          className='navButton1'
                          disabled={indexOfLastRecord >= totalCount}
                          onClick={() => handlePageChange(currentPage + 1)}
                        >
                          {t('pagination.Next')}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )
          }
        </div>
      </div>

      <Footer />
    </>
  );
};

export default FavouriteList;
